import { useCallback, useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function detectStandalone(): boolean {
    if (typeof window === "undefined") return false;
    return (
        window.matchMedia("(display-mode: standalone)").matches ||
        // iOS Safari legacy flag
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true
    );
}

function detectIOS(): boolean {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent;
    const isIPad =
        navigator.maxTouchPoints > 1 && /Macintosh/.test(ua) && !("MSStream" in window);
    return /iPhone|iPad|iPod/.test(ua) || isIPad;
}

/**
 * Add-to-home-screen support:
 * - Android/Chromium fires `beforeinstallprompt`, which we capture so the app
 *   can show its own install button (`canInstall` + `promptInstall`).
 * - iOS Safari has no prompt API; `isIOS` lets the UI show manual
 *   "Share → Add to Home Screen" instructions instead.
 */
export function usePWAInstall() {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [isStandalone, setIsStandalone] = useState(detectStandalone);
    const [installed, setInstalled] = useState(false);

    useEffect(() => {
        const onBeforeInstall = (event: Event) => {
            event.preventDefault();
            setDeferredPrompt(event as BeforeInstallPromptEvent);
        };
        const onInstalled = () => {
            setDeferredPrompt(null);
            setInstalled(true);
        };
        const media = window.matchMedia("(display-mode: standalone)");
        const onDisplayModeChange = () => setIsStandalone(detectStandalone());

        window.addEventListener("beforeinstallprompt", onBeforeInstall);
        window.addEventListener("appinstalled", onInstalled);
        media.addEventListener("change", onDisplayModeChange);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            window.removeEventListener("appinstalled", onInstalled);
            media.removeEventListener("change", onDisplayModeChange);
        };
    }, []);

    const promptInstall = useCallback(async () => {
        if (!deferredPrompt) return false;
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === "accepted") {
            setDeferredPrompt(null);
        }
        return outcome === "accepted";
    }, [deferredPrompt]);

    return {
        canInstall: deferredPrompt !== null && !isStandalone && !installed,
        promptInstall,
        isStandalone,
        isIOS: detectIOS(),
        installed,
    };
}
